// How a person is drawn, in one place.
//
// Two tiny functions, shared rather than copied, because the account record and
// the sourcing wizard show the same human and must not disagree about them. A
// second copy of the seniority ramp is a second copy that goes stale, and the
// symptom — a founder rendered one shade on one screen and another shade on the
// next — reads as a bug in the data rather than in the CSS.

import { seniorityFromTitle } from '../lib/persona-match.js';

/**
 * Initials, for a name whose surname is still obfuscated ("Marca Ar***g").
 *
 * There are no photographs of these people and there never will be — they are
 * strangers pulled from a database. Initials in a circle is the cheapest honest
 * way to make a list read as people rather than as table rows, which is the
 * whole "humanise B2B" argument applied to what actually exists.
 */
export function monogram(name: string | null | undefined): string {
  return String(name ?? '')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join('').toUpperCase() || '?';
}

/**
 * The cool emphasis ramp, low → high, keyed by seniority.
 *
 * SENIORITY EARNS WEIGHT. A founder must not render at the same value as a
 * marketing manager on a list whose entire purpose is being read top-down. The
 * deep end of the ramp is for the people you would actually enter through; the
 * pale end is support. Using pale grey for hero data is the mistake that makes
 * a branded surface look like default Excel.
 *
 * Falls back through the title when a row has no seniority — Apollo's free
 * search does not return one, so most rows arrive with only a title.
 */
const RAMP: Record<string, string> = {
  owner: '#284C86', founder: '#284C86', c_suite: '#284C86',
  partner: '#4F6088', vp: '#4F6088',
  head: '#7E91B0', director: '#7E91B0',
  manager: '#A9B2C2', senior: '#A9B2C2',
};

export function rampOf(seniorityOrTitle: string | null | undefined): string {
  const key = String(seniorityOrTitle ?? '');
  return RAMP[key] ?? RAMP[seniorityFromTitle(key)] ?? '#CBD0D8';
}
